// The payroll register — what each person is owed for a month, and why.
//
// The database computes it (20260829120000_payroll.sql). Nothing here calculates
// money: `runPeriod` calls the SECURITY DEFINER function, and everything else
// reads rows it wrote. A figure invented on the client would be a second answer
// to a question Postgres already answers, and the wrong one to trust.
//
// ⚠️ `payable` is NOT take-home pay. It is gross, less the attendance shortfall
// deduction, plus any prior-period adjustment. PF, ESI and TDS are out of scope
// and are not applied anywhere in this system. Every surface that renders it says
// so in words — do not shorten that caption away.
import { supabase } from './supabase';
import { write } from './write';

export type PayrollLine = {
  id: string;
  period_year: number;
  period_month: number;
  profile_id: string;
  gross: number;
  required_hours: number;
  worked_hours: number;
  compensated_hours: number;
  effective_hours: number;
  shortfall_hours: number;
  deduction_amount: number;
  adjustment_amount: number;
  payable: number;
  auto_closed_days: number;
  /**
   * Hours of APPROVED unpaid leave inside the priced window. Explains part of
   * `shortfall_hours` — it is not subtracted from anything, and the deduction is
   * identical with or without it. `null` on lines priced before 20260916100000,
   * which means "unknown", not "none".
   */
  unpaid_leave_hours: number | null;
  /** 20261009110000: under the waiver threshold, so nothing was deducted. */
  shortfall_waived?: boolean;
  /** Days of leave the run took instead of pay, and from which type. */
  shortfall_leave_days?: number;
  shortfall_leave_type?: string | null;
};

/** A register row, with the person's name resolved. */
export type RegisterRow = PayrollLine & {
  full_name: string | null;
  department: string | null;
  /**
   * The month's overtime and the comp off it was credited as, from
   * overtime_settlement (20261008110000). Both `null` until the month is
   * settled, which happens when payroll locks — "not yet", not "none".
   */
  overtime_hours: number | null;
  overtime_leave: number | null;
  /** HR's pick for a shortfall: 'pay', or 'leave:<type id>'. '' when unset (pay). */
  shortfall_choice: string;
};

export type Component = { kind: string; label: string; amount: number; sort: number };

const SELECT =
  'id, period_year, period_month, profile_id, gross, required_hours, worked_hours, ' +
  'compensated_hours, effective_hours, shortfall_hours, deduction_amount, ' +
  'adjustment_amount, payable, auto_closed_days, unpaid_leave_hours, ' +
  'shortfall_waived, shortfall_leave_days, shortfall_leave_type';

/** The caption every money surface carries. One string, so it cannot drift. */
export const PRE_STATUTORY_NOTE =
  'Payable before statutory deductions — PF, ESI and TDS are not applied.';

/** A read's rows, or why there are none. A failed read is not an empty list
 *  (UI-CONVENTIONS §7): the caller renders LoadError on `error`, never "nothing yet". */
export type Read<T> = { data: T; error: { message: string } | null };

export async function loadRegister(year: number, month: number): Promise<Read<RegisterRow[]>> {
  if (!supabase) return { data: [], error: null };
  const monthKey = `${year}-${String(month).padStart(2, '0')}-01`;
  const [{ data, error }, settled, choices] = await Promise.all([
    supabase
      .from('payroll_line')
      // Named FK: payroll_line references profiles twice (profile_id, computed_by),
      // so a bare embed is ambiguous and PostgREST refuses the whole query.
      .select(`${SELECT}, profiles!payroll_line_profile_id_fkey(full_name, departments(name))`)
      .eq('period_year', year)
      .eq('period_month', month),
    supabase
      .from('overtime_settlement')
      .select('profile_id, ot_seconds, leaves_credited')
      .eq('month', monthKey),
    supabase
      .from('payroll_shortfall_choice')
      .select('profile_id, method, leave_type_id')
      .eq('year', year)
      .eq('month', month),
  ]);
  const choice = new Map(
    (
      (choices.data as { profile_id: string; method: string; leave_type_id: string | null }[]) ?? []
    ).map((c) => [c.profile_id, c.method === 'leave' ? `leave:${c.leave_type_id}` : 'pay']),
  );
  const ot = new Map(
    (
      (settled.data as { profile_id: string; ot_seconds: number; leaves_credited: number }[]) ?? []
    ).map((s) => [s.profile_id, s]),
  );
  const rows = ((data as Record<string, unknown>[]) ?? []).map((r) => {
    const p = Array.isArray(r.profiles) ? r.profiles[0] : r.profiles;
    // The department over its FK, never the legacy profiles.department text.
    const prof = p as { full_name?: string | null; departments?: { name: string } | null } | null;
    const s = ot.get(r.profile_id as string);
    return {
      ...(r as unknown as PayrollLine),
      full_name: prof?.full_name ?? null,
      department: prof?.departments?.name ?? null,
      overtime_hours: s ? Math.round((s.ot_seconds / 3600) * 10) / 10 : null,
      overtime_leave: s ? Number(s.leaves_credited) : null,
      shortfall_choice: choice.get(r.profile_id as string) ?? '',
    };
  });
  return { data: rows, error: error ?? settled.error ?? choices.error };
}

/** Paid leave types a shortfall may be taken from (the trigger refuses others). */
export async function shortfallLeaveTypes(): Promise<{ id: string; label: string }[]> {
  if (!supabase) return [];
  const { data } = await supabase
    .from('leave_types')
    .select('id, label')
    .eq('paid', true)
    .eq('archived', false)
    .order('rank');
  return (data as { id: string; label: string }[]) ?? [];
}

/**
 * HR's pick for one person's shortfall in an open month: 'pay', or
 * 'leave:<type id>'. Update first, insert if there was no row: the column
 * grants allow updating only the choice, so an upsert (which re-sets the key
 * columns) would be refused.
 */
export async function setShortfallChoice(
  profileId: string,
  year: number,
  month: number,
  value: string,
): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const leave = value.startsWith('leave:');
  const fields = { method: leave ? 'leave' : 'pay', leave_type_id: leave ? value.slice(6) : null };
  const upd = await supabase
    .from('payroll_shortfall_choice')
    .update(fields)
    .eq('profile_id', profileId)
    .eq('year', year)
    .eq('month', month)
    .select('profile_id');
  if (upd.error) return { error: upd.error };
  if (upd.data?.length) return { error: null };
  const { error } = await supabase
    .from('payroll_shortfall_choice')
    .insert({ profile_id: profileId, year, month, ...fields });
  return { error };
}

/** The signed-in person's own statements. RLS returns only closed months. */
export async function myStatements(uid: string): Promise<Read<PayrollLine[]>> {
  if (!supabase) return { data: [], error: null };
  const { data, error } = await supabase
    .from('payroll_line')
    .select(SELECT)
    .eq('profile_id', uid)
    .order('period_year', { ascending: false })
    .order('period_month', { ascending: false })
    .limit(12);
  return { data: (data as unknown as PayrollLine[]) ?? [], error };
}

export async function loadComponents(lineId: string): Promise<Read<Component[]>> {
  if (!supabase) return { data: [], error: null };
  const { data, error } = await supabase
    .from('payroll_line_component')
    .select('kind, label, amount, sort')
    .eq('line_id', lineId)
    .order('sort');
  return { data: (data as Component[]) ?? [], error };
}

type Result = { error: { message: string } | null };

export async function runPeriod(year: number, month: number): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.rpc('payroll_run', { p_year: year, p_month: month });
  return { error };
}

export async function settleAmendment(year: number, month: number): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  const { error } = await supabase.rpc('settle_period_amendment', {
    p_year: year,
    p_month: month,
  });
  return { error };
}

export async function markPaid(year: number, month: number): Promise<Result> {
  if (!supabase) return { error: { message: 'Not connected.' } };
  // Through write(): payroll_period keys on (year, month) and has no `id`, and
  // an update RLS filters out is zero rows with no error — "paid" that was not.
  const r = await write(
    supabase
      .from('payroll_period')
      .update({ status: 'paid', paid_at: new Date().toISOString() })
      .eq('year', year)
      .eq('month', month),
    'year',
  );
  if (r.ok) return { error: null };
  return {
    error: r.reason === 'refused' ? { message: 'You can’t mark this month paid.' } : r.error,
  };
}

/** One closed month, summed across everyone priced in it. */
export type CostMonth = {
  year: number;
  month: number;
  payable: number;
  gross: number;
  deduction: number;
  shortfallHours: number;
  autoClosedDays: number;
  people: number;
};

/**
 * Payroll for the `months` calendar months ending at (year, month), summed per
 * month, oldest first.
 *
 * This adds up figures Postgres already computed; it does not price anything.
 * The module rule at the top of this file stands — `payable` per person is the
 * database's answer and this only totals the column.
 *
 * A month with no payroll_line rows is OMITTED, never returned as a zero. Zero
 * payroll is not a fact about a month nobody ran: `payroll_run` writes rows only
 * when somebody executes it, so the series is sparse by construction and a zero
 * bar would assert something the table never said. It is bounded to the real
 * window rather than "the last n months that happen to have rows" — otherwise a
 * card headed "last 6 months" could show a row from two years ago.
 *
 * ⚠️ Gate the CALLER on people:full. pl_read also admits a person's own rows
 * once their period is locked/paid/amended, so below that gate this returns a
 * real, non-empty set — one person's salary history — which summed under a
 * heading saying "team" is a confident lie rather than an empty card.
 */
export async function loadCostTrend(
  year: number,
  month: number,
  months = 6,
): Promise<Read<CostMonth[]>> {
  if (!supabase) return { data: [], error: null };

  // The window, as explicit year/month fields. Built by arithmetic on a month
  // index rather than Date maths, because setMonth() overflows on the 31st.
  const endIdx = year * 12 + (month - 1);
  const startIdx = endIdx - (months - 1);
  const inWindow = (y: number, m: number) => {
    const i = y * 12 + (m - 1);
    return i >= startIdx && i <= endIdx;
  };

  // `.in` on the year rather than a >=(year,month) predicate: PostgREST needs an
  // .or() with a nested and() for that, and it is easy to get subtly wrong. The
  // window is filtered client-side below.
  //
  // ORDER AND LIMIT ARE NOT OPTIONAL. supabase/config.toml sets max_rows = 1000,
  // so an unbounded read is silently truncated — and without an order, WHICH
  // rows survive is arbitrary, so a month would quietly under-sum with no error
  // anywhere. Ordered newest-first and capped at a generous multiple of the
  // window, the cap can only ever drop months already outside it.
  const years = [...new Set([Math.floor(startIdx / 12), year])];
  const { data, error } = await supabase
    .from('payroll_line')
    .select(
      'period_year, period_month, gross, payable, deduction_amount, shortfall_hours, auto_closed_days',
    )
    .in('period_year', years)
    .order('period_year', { ascending: false })
    .order('period_month', { ascending: false })
    .limit(1000);

  const byKey = new Map<string, CostMonth>();
  for (const r of (data as unknown as PayrollLine[]) ?? []) {
    if (!inWindow(r.period_year, r.period_month)) continue;
    const key = `${r.period_year}-${String(r.period_month).padStart(2, '0')}`;
    const m = byKey.get(key) ?? {
      year: r.period_year,
      month: r.period_month,
      payable: 0,
      gross: 0,
      deduction: 0,
      shortfallHours: 0,
      autoClosedDays: 0,
      people: 0,
    };
    m.payable += r.payable || 0;
    m.gross += r.gross || 0;
    m.deduction += r.deduction_amount || 0;
    m.shortfallHours += Number(r.shortfall_hours) || 0;
    m.autoClosedDays += r.auto_closed_days || 0;
    m.people += 1;
    byKey.set(key, m);
  }

  return {
    data: [...byKey.values()].sort((a, b) => a.year - b.year || a.month - b.month),
    error,
  };
}
